/**
 * Histórico da última hora por dispositivo (da base, para os gráficos dos cards) — Nexora Grid.
 *
 * Criado em: 17/10/2026 · Última modificação: 18/10/2026 · Versão: 1.1.0
 *
 * v1.1 (GUS-1504): medidas da VRP e do reservatório (jusante, montante, entrada e saída) para os cards da tela nova.
 *
 * Retorno do responsável do produto (17/10/2026): "os gráficos abaixo dos cards estão muito ruins; precisamos trazer
 * 1 hora de dados no gráfico, trazendo da base, estilo Grafana". Um pedido por dispositivo (todas as medidas),
 * compartilhado pelos cards e atualizado a cada minuto (`GET /api/v1/tempo-real/historico`).
 */
import { useEffect, useState } from "react";
import { api } from "../api";

export type Serie = { t: number; v: number }[];
export type ComparacaoOntem = { serie: Serie; estado: "carregando" | "pronto" | "erro" };
type Entrada = { series: Record<string, Serie>; carregadoEm: number; pedido?: Promise<void> };

const cache = new Map<string, Entrada>();
const ouvintes = new Map<string, Set<() => void>>();
const MEDIDAS_HISTORICO = ["nivel_reservatorio_pct", "vazao_instantanea_m3h", "pressao_recalque_bar", "corrente_fase_a", "tensao_fase_v",
  "potencia_ativa_kw", "frequencia_inversor_hz", "temperatura_bomba_c", "volume_total_acumulado_m3", "sinal_analogico_ma",
  "nivel_m", "pressao_rede_mca", "pressao_jusante_mca", "pressao_montante_mca", "vazao_entrada_m3h", "vazao_saida_m3h",
  "pressao_recalque_mca", "pressao_mca"];

function carregar(empresa: string, device: string): Promise<void> {
  const k = `${empresa}|${device}`;
  const atual = cache.get(k);
  if (atual?.pedido) return atual.pedido;
  const pedido = api<{ series: Record<string, [string, number][]> }>(
    `/v1/tempo-real/historico?empresa=${empresa}&dispositivo=${encodeURIComponent(device)}&medidas=${MEDIDAS_HISTORICO.join(",")}&minutos=60`)
    .then((r) => {
      const series: Record<string, Serie> = {};
      Object.entries(r.series).forEach(([m, pts]) => { series[m] = pts.map(([t, v]) => ({ t: Date.parse(t), v })); });
      cache.set(k, { series, carregadoEm: Date.now() });
    })
    .catch(() => { cache.set(k, { series: atual?.series ?? {}, carregadoEm: Date.now() }); })
    .finally(() => ouvintes.get(k)?.forEach((f) => f()));
  cache.set(k, { series: atual?.series ?? {}, carregadoEm: atual?.carregadoEm ?? 0, pedido });
  return pedido;
}

/** Série da última hora de uma medida do dispositivo (compartilhada entre os cards do mesmo dispositivo). */
export function useHistoricoHora(empresa: string | null, device: string, medida: string): Serie {
  const [, setVersao] = useState(0);
  const k = `${empresa}|${device}`;
  useEffect(() => {
    if (!empresa) return;
    const atualizar = () => setVersao((n) => n + 1);
    if (!ouvintes.has(k)) ouvintes.set(k, new Set());
    ouvintes.get(k)!.add(atualizar);
    const entrada = cache.get(k);
    if (!entrada || Date.now() - entrada.carregadoEm > 55000) void carregar(empresa, device);
    const relogio = window.setInterval(() => void carregar(empresa, device), 60000);
    return () => { ouvintes.get(k)?.delete(atualizar); window.clearInterval(relogio); };
  }, [empresa, device, k]);
  return cache.get(k)?.series[medida] ?? [];
}

/** Mesma janela de uma hora, 24 horas antes; mantém as datas originais para identificação no gráfico. */
export function useHistoricoOntem(empresa: string | null, device: string, medida: string): ComparacaoOntem {
  const chave = `${empresa}|${device}|${medida}`;
  const [dados, setDados] = useState<ComparacaoOntem & { chave: string }>({ chave: "", serie: [], estado: "carregando" });
  useEffect(() => {
    if (!empresa) return;
    const controller = new AbortController();
    let pedido = false;
    const carregar = async () => {
      if (pedido) return;
      pedido = true;
      try {
        const r = await api<{ series: Record<string, [string, number][]> }>(
          `/v1/tempo-real/historico?empresa=${encodeURIComponent(empresa)}&dispositivo=${encodeURIComponent(device)}&medidas=${encodeURIComponent(medida)}&minutos=60&deslocamento_minutos=1440`);
        if (!controller.signal.aborted) setDados({ chave, estado: "pronto",
          serie: (r.series[medida] ?? []).map(([t, v]) => ({ t: Date.parse(t), v })) });
      } catch {
        if (!controller.signal.aborted) setDados({ chave, serie: [], estado: "erro" });
      } finally { pedido = false; }
    };
    void carregar();
    const relogio = window.setInterval(() => void carregar(), 60000);
    return () => { controller.abort(); window.clearInterval(relogio); };
  }, [empresa, device, medida, chave]);
  return dados.chave === chave ? dados : { serie: [], estado: "carregando" };
}
